/**
 * Merge multiple per-key analytics payloads into one object for the "All keys" view.
 * Sums usage metrics and merges chain / time-series data by key name.
 */
export function mergeAnalyticsPayloads(items) {
  if (!items || items.length === 0) return null;
  if (items.length === 1) return items[0];

  const result = JSON.parse(JSON.stringify(items[0]));

  result.total_requests = items.reduce(
    (s, x) => s + (Number(x.total_requests) || 0),
    0,
  );
  const n = items.length;
  result.daily_average =
    items.reduce((s, x) => s + (Number(x.daily_average) || 0), 0) / n;

  // limits.monthly_used / daily_used are now ORG-LEVEL counters returned by
  // the backend (every key's analytics response carries the same org-wide
  // total — single source of truth, matches what the gateway enforces). So
  // we DO NOT sum across keys; we take the first non-zero (or zero if no
  // requests yet). Pre-org-rate-limit this used to sum because each key had
  // its own quota counter.
  const L0 = items[0].limits || {};
  const L = { ...L0 };
  const firstNonZero = (k) => {
    for (const x of items) {
      const v = Number(x.limits?.[k]);
      if (Number.isFinite(v) && v !== 0) return v;
    }
    return Number(items[0].limits?.[k]) || 0;
  };
  L.monthly_used = firstNonZero("monthly_used");
  L.daily_used = firstNonZero("daily_used");
  L.archive_used = firstNonZero("archive_used");
  // full_used is an org-wide raw bucket counter — same first-non-zero rule.
  // archive_weight is a plan field, take it from any item (they all carry
  // the same plan). The "special" bucket was removed: per-chain
  // weight_override is now applied as a multiplier on full/archive, so the
  // bucket model is simply full + archive.
  L.full_used = firstNonZero("full_used");
  if (L.archive_weight == null) {
    for (const x of items) {
      const w = x.limits?.archive_weight;
      if (w != null) { L.archive_weight = w; break; }
    }
  }
  if (L.monthly_limit != null) {
    L.monthly_remaining = firstNonZero("monthly_remaining");
  }
  if (L.daily_limit != null) {
    L.daily_remaining = firstNonZero("daily_remaining");
  }
  if (L.archive_limit != null) {
    L.archive_remaining = firstNonZero("archive_remaining");
  }
  result.limits = L;

  if (items[0].current_plan) {
    result.current_plan = items[0].current_plan;
  }

  // status_breakdown is per-key 2xx/4xx/5xx counts — sum across keys to get
  // the org-wide tally used for the success-rate insight card.
  const sb = { total: 0, success_2xx: 0, errors_4xx: 0, errors_5xx: 0 };
  for (const p of items) {
    const s = p.status_breakdown || {};
    sb.total += Number(s.total) || 0;
    sb.success_2xx += Number(s.success_2xx) || 0;
    sb.errors_4xx += Number(s.errors_4xx) || 0;
    sb.errors_5xx += Number(s.errors_5xx) || 0;
  }
  result.status_breakdown = sb;

  const chainMap = new Map();
  for (const p of items) {
    for (const c of p.chain_stats || []) {
      const k = c.chain;
      if (!chainMap.has(k)) {
        chainMap.set(k, {
          chain: k,
          total: 0,
          daily: 0,
          monthly: 0,
        });
      }
      const agg = chainMap.get(k);
      agg.total += Number(c.total) || 0;
      agg.daily += Number(c.daily) || 0;
      agg.monthly += Number(c.monthly) || 0;
    }
  }
  const chainTotals = [...chainMap.values()];
  const sumTotal = chainTotals.reduce((s, c) => s + c.total, 0) || 1;
  result.chain_stats = chainTotals.map((c) => ({
    ...c,
    percentage: Math.round((c.total / sumTotal) * 1000) / 10,
  }));

  const bcMap = new Map();
  for (const p of items) {
    for (const row of p.by_chain || []) {
      const k = row.chain;
      bcMap.set(k, (bcMap.get(k) || 0) + (Number(row.count) || 0));
    }
  }
  result.by_chain = [...bcMap.entries()].map(([chain, count]) => ({
    chain,
    count,
  }));

  const dateMap = new Map();
  for (const p of items) {
    for (const row of p.by_date_chain || []) {
      const d = row.date;
      if (!dateMap.has(d)) {
        dateMap.set(d, { date: d });
      }
      const agg = dateMap.get(d);
      for (const [key, val] of Object.entries(row)) {
        if (key === "date") continue;
        agg[key] = (agg[key] || 0) + (Number(val) || 0);
      }
    }
  }
  result.by_date_chain = [...dateMap.values()].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  return result;
}
